import { styled } from '@linaria/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PaceStatus } from '../../../api/work-periods-client/work-periods-client.type';
import { ProgressMeasure } from '../../../components/progress-bar/progress-bar-measure';
import { coerceToDateInput, formatDate } from '../../../helpers/date-time-helpers';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { Metric, type PaceChartDataPoint, WorkPaceChartType } from '../work-periods.type';
import { getScopeMetadata, getTasksCompleteMetadata, getWorkPeriodBurnedMetadata } from './pace-section.helpers';

type Props = {
  chartType: WorkPaceChartType;
  chartData: PaceChartDataPoint[];
  selectedDate: string | null;
  handleDoneClick: (date?: string | null) => void;
};

/**
 * Presentational Recharts line chart for the Work Pace tab. Renders the
 * Actual / Ideal / Total series, the static side-rail Progress measures
 * (Done, Burnt, Scope) anchored to the parent's `selectedDate`, and a custom
 * Recharts Tooltip. Nullified data rows (future dates upstream-trimmed by
 * `displayChartData`) are short-circuited in both the dot render funcs and
 * the tooltip so the user never sees `null` values for unrendered days.
 *
 * @param {Props} props - Chart props
 * @param {WorkPaceChartType} props.chartType - BurnUp vs BurnDown variant;
 *   controls the Done/Remaining label and the conditional Total line.
 * @param {PaceChartDataPoint[]} props.chartData - Pre-trimmed display chart
 *   data (future-of-today rows have nullified line-series fields).
 * @param {string | null} props.selectedDate - Lookup-safe anchor date for the
 *   side-rail Progress measures; never a future date.
 * @param {(date?: string | null) => void} props.handleDoneClick - Drill-in
 *   callback; receives a clicked-dot's date or defaults to `selectedDate`.
 * @returns {JSX.Element} The chart UI
 */
export const WorkPaceChart = ({ chartType, chartData, selectedDate, handleDoneClick }: Props) => {
  const i18n = useTranslation();
  const { selectedMetric: metricType } = useWorkPeriodStore();

  const getMetricLabel = useCallback(
    (metric: Metric): string => {
      return metric === Metric.Tasks
        ? i18n.t('common.work_units').toLowerCase()
        : i18n.t('common.estimation').toLowerCase();
    },
    [i18n.t],
  );

  return (
    <ChartWrapper>
      <MainContentContainer>
        <SideMetricsContainer>
          <ProgressMeasure
            title={chartType === WorkPaceChartType.BurnDown ? 'Remaining' : 'Done'}
            percent={
              chartType === WorkPaceChartType.BurnDown
                ? 100 - getTasksCompleteMetadata(chartData, selectedDate, metricType, i18n.t).percent
                : getTasksCompleteMetadata(chartData, selectedDate, metricType, i18n.t).percent
            }
            icon={chartType === WorkPaceChartType.BurnDown ? 'mdi:clock-outline' : 'mdi:check-circle'}
            color={chartType === WorkPaceChartType.BurnDown ? '#4C6EF5' : '#40C057'}
            helpText={
              chartType === WorkPaceChartType.BurnDown
                ? (() => {
                    const currentPoint = chartData.find((p) => p.date === selectedDate);
                    const total = currentPoint?.total ?? 0;
                    const complete = currentPoint?.complete ?? 0;
                    return `${total - complete} of ${total} ${getMetricLabel(metricType)} remaining`;
                  })()
                : getTasksCompleteMetadata(chartData, selectedDate, metricType, i18n.t).helpText
            }
          />
          <ProgressMeasure
            title="Burnt"
            percent={getWorkPeriodBurnedMetadata(chartData, selectedDate).percent}
            icon="mdi:fire"
            color="#4C6EF5"
            helpText={getWorkPeriodBurnedMetadata(chartData, selectedDate).helpText}
          />
          <ProgressMeasure
            title="Scope"
            percent={getScopeMetadata(chartData, selectedDate, metricType, i18n.t).percent}
            icon="mdi:flag-checkered"
            color="#312e81"
            helpText={getScopeMetadata(chartData, selectedDate, metricType, i18n.t).helpText}
          />
        </SideMetricsContainer>
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(value, { format: 'MMM D' })}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                label={{
                  value:
                    chartType === WorkPaceChartType.BurnDown
                      ? metricType === Metric.Tasks
                        ? `${i18n.t('common.work_units')} Remaining`
                        : `${i18n.t('common.estimation')} Remaining`
                      : metricType === Metric.Tasks
                        ? `${i18n.t('common.work_units')} Completed`
                        : `${i18n.t('common.estimation')} Completed`,
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' },
                  offset: 10,
                }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'actual' || name === 'ideal') {
                    return [`${value} ${getMetricLabel(metricType)}`, name];
                  }
                  return [value, name];
                }}
                labelFormatter={(label) => `Date: ${formatDate(coerceToDateInput(label), { format: 'MMM D, YYYY' })}`}
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    if (!data) return null;
                    // chartData rows past today are nullified by
                    // `displayChartData` upstream. Suppress the tooltip for
                    // those points so we don't render "Total: null tasks /
                    // Status: Off Track" when the user hovers a future date.
                    if (data.complete == null) return null;
                    const statusText =
                      data.status === PaceStatus.OnTrack
                        ? 'On Track'
                        : data.status === PaceStatus.AtRisk
                          ? 'At Risk'
                          : 'Off Track';

                    return (
                      <TooltipContainer>
                        <TooltipHeader>
                          {`Date: ${formatDate(coerceToDateInput(label), { format: 'MMM D, YYYY' })}`}
                        </TooltipHeader>
                        <TooltipRow>
                          <TooltipLabel>Total:</TooltipLabel>
                          <TooltipValue>{`${data.total} ${getMetricLabel(metricType)}`}</TooltipValue>
                        </TooltipRow>
                        <TooltipRow>
                          <TooltipLabel>
                            {chartType === WorkPaceChartType.BurnDown ? 'Remaining:' : 'Actual:'}
                          </TooltipLabel>
                          <TooltipValue>{`${data.complete} ${getMetricLabel(metricType)}`}</TooltipValue>
                        </TooltipRow>
                        <TooltipRow>
                          <TooltipLabel>
                            {chartType === WorkPaceChartType.BurnDown ? 'Ideal Remaining:' : 'Ideal:'}
                          </TooltipLabel>
                          <TooltipValue>{`${data.ideal} ${getMetricLabel(metricType)}`}</TooltipValue>
                        </TooltipRow>
                        <TooltipRow>
                          <TooltipLabel>Status:</TooltipLabel>
                          <TooltipStatus color={data.statusColor}>{statusText}</TooltipStatus>
                        </TooltipRow>
                      </TooltipContainer>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Line
                key={`${chartType}-${metricType}-complete`}
                type="monotone"
                dataKey="complete"
                name={chartType === WorkPaceChartType.BurnDown ? 'Remaining' : 'Actual'}
                stroke={chartType === WorkPaceChartType.BurnDown ? '#FA5252' : '#4C6EF5'}
                strokeWidth={2}
                isAnimationActive={false}
                dot={(props: any) => {
                  const { cx, cy, payload, index } = props;
                  // Recharts invokes the custom dot render for every data point including
                  // nullified ones. Returning null is the documented skip pattern — see
                  // https://github.com/recharts/recharts/discussions/5533. Recharts coerces
                  // null into `<g />` internally so the render-prop contract is preserved.
                  if (payload?.complete == null) return <g key={`empty-${payload?.date || index}`} />;
                  return (
                    <circle
                      key={`dot-${payload.date || index}`}
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill={payload.statusColor}
                      stroke={chartType === WorkPaceChartType.BurnDown ? '#FA5252' : '#4C6EF5'}
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={(props: any) => {
                  const { cx, cy, payload, index } = props;
                  if (payload?.complete == null) return <g key={`empty-${payload?.date || index}`} />;
                  return (
                    <circle
                      key={`activedot-${payload.date || index}`}
                      cx={cx}
                      cy={cy}
                      r={7}
                      fill={payload.statusColor}
                      stroke={chartType === WorkPaceChartType.BurnDown ? '#FA5252' : '#4C6EF5'}
                      strokeWidth={2}
                      cursor="pointer"
                      onClick={() => handleDoneClick(payload?.date ?? null)}
                    />
                  );
                }}
              />
              <Line
                key={`${chartType}-${metricType}-ideal`}
                type="monotone"
                dataKey="ideal"
                name={chartType === WorkPaceChartType.BurnDown ? 'Ideal Remaining' : 'Ideal'}
                stroke={chartType === WorkPaceChartType.BurnDown ? '#4C6EF5' : '#40C057'}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
              />
              {chartType === WorkPaceChartType.BurnUp && (
                <Line
                  key={`${chartType}-${metricType}-total`}
                  type="monotone"
                  dataKey="total"
                  name="Total"
                  stroke="#312e81"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </MainContentContainer>
    </ChartWrapper>
  );
};

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const MainContentContainer = styled.div`
  display: flex;
  gap: 24px;
  flex: 1;
  min-height: 300px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;
`;

const SideMetricsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 250px;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const TooltipContainer = styled.div`
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TooltipHeader = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const TooltipLabel = styled.span`
  font-weight: 500;
  margin-right: 8px;
`;

const TooltipValue = styled.span`
  font-weight: normal;
`;

const TooltipStatus = styled.span<{ color: string }>`
  font-weight: bold;
  color: ${(props) => props.color};
`;
